"use client";

import AccreditationCard from "@/components/atoms/AccreditationCard";
import LoadMore from "@/components/atoms/LoadMore";
import Carousel10 from "@/components/organisms/carousels/Carousel10";
import Carousel from "@/components/organisms/carousels/Carousel5";
import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  carouselClick,
} from "@/customAnalytics/trackingEvents";
import cn from "classnames";
import { useCallback, useMemo, useRef, useState } from "react";

interface MetaItem {
  id: string;
  meta_logo: string;
  meta_description: string;
  meta_priority: number;
  meta_alt_image?: string;
  meta_description_text_color?: string;
  meta_status?: boolean;
}

interface AccreditationData {
  id: number;
  page_id: number;
  title: string;
  title_text_color?: string;
  sub_title?: string;
  sub_title_text_color?: string;
  background_color?: string;
  is_swipe?: number; // 0 = load more, 1 = swipe carousel
  slide_frequency: number;
  left_navigation_icon?: string;
  left_navigation_icon_alt?: string;
  right_navigation_icon?: string;
  right_navigation_icon_alt?: string;
  load_more_text?: string;
  load_more_color?: string;
  load_more_background_color?: string;
  load_more_icon?: string;
  load_more_icon_alt?: string;

  metadata?: MetaItem[];
  status?: boolean;
}

interface AccreditationProps {
  data: AccreditationData;
}

const ITEMS_PER_PAGE = 4;

// Removed useIsMobile because it causes the LCP to be delayed until the JS is loaded and executed, which hurts performance. Instead, we'll use CSS media queries to handle responsive design.
// ealier it was this --> const isMobile = useIsMobile(800);
const MOBILE_BREAKPOINT = 800;

const Accreditation1 = ({ data }: { data: AccreditationProps }) => {
  // Hooks must be called before early returns
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const ref = useRef<HTMLElement>(null);
  const previousIndexRef = useRef<number>(-1);

  const {
    title = "",
    title_text_color = "#000000",
    sub_title = "",
    sub_title_text_color = "#000000",
    is_swipe = 0,
    metadata = [],
    left_navigation_icon,
    left_navigation_icon_alt,
    right_navigation_icon,
    right_navigation_icon_alt,
    load_more_text = "",
    load_more_color,
    load_more_icon,
    load_more_icon_alt,
    background_color = "#FFFFFF",
  } = data?.data || {};

  // Sort metadata by priority - MUST be called before early returns
  const sortedMetadata = useMemo(() => {
    if (!metadata || metadata.length === 0) return [];
    return [...metadata]
      .filter((item: MetaItem) => item?.meta_status == true)
      .sort((a: MetaItem, b: MetaItem) => a.meta_priority - b.meta_priority);
  }, [metadata]);

  // Track widget viewed
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "accreditation_section");
  //   }
  // }, []);

  // Track carousel slide changes
  const handleSlideChange = useCallback(
    (index: number) => {
      // Only track if slide actually changed (not initial render)
      if (
        previousIndexRef.current !== index &&
        previousIndexRef.current !== -1 &&
        sortedMetadata &&
        sortedMetadata.length > 0
      ) {
        const currentItem = sortedMetadata[index];
        carouselClick({
          section: "accreditation_section",
          label:
            currentItem?.meta_description ||
            title ||
            `accreditation_${index + 1}`,
        });
      }
      previousIndexRef.current = index;
    },
    [sortedMetadata, title],
  );

  if (!data?.data?.status) {
    return null;
  }

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
  };

  const hasMore = sortedMetadata && visibleCount < sortedMetadata.length;

  if (sortedMetadata?.length === 0 || !sortedMetadata) {
    return null;
  }

  return (
    <section
      ref={ref}
      style={{ backgroundColor: background_color || "#FFFFFF" }}
      className={cn(
        "flex justify-center w-full items-center",
        "px-5 py-8 sm:px-9 sm:py-11",
      )}
    >
      <div
        className={cn(
          "flex flex-col items-center justify-center gap-spacing-24 w-full",
        )}
      >
        {/* Header Section */}
        <div className="flex flex-col items-center justify-center gap-3 text-center w-full">
          <h2
            className={cn(
              "text-center",
              "text-heading-6-semibold",
              "sm:text-heading-5-semibold",
              "md:text-heading-4-semibold",
              "lg:text-new-heading-34-semibold",
              "xl:text-new-heading-42-semibold",
              "line-clamp-1",
              "type-h3",
            )}
            style={{ color: title_text_color }}
          >
            {title}
          </h2>
          {sub_title && (
            <p
              className={cn(
                "text-center",
                "text-body-2-regular",
                "sm:text-body-3-regular",
                "md:text-body-2-regular",
                "lg:text-body-1-regular",
                "xl:text-new-body-24-regular",
                "line-clamp-2 sm:line-clamp-1",
                "type-body-sm",
              )}
              style={{ color: sub_title_text_color }}
            >
              {sub_title}
            </p>
          )}
        </div>

        {/* Content Section */}
        {/* for mobiles */}
        {is_swipe === 1 ? (
          // Swipe carousel mode
          <div
            className={`min-[${MOBILE_BREAKPOINT}px]:hidden flex justify-center items-center gap-spacing-14 w-full overflow-x-hidden flex-nowrap`}
          >
            <Carousel10>
              {sortedMetadata?.map((item, index) => (
                <AccreditationCard
                  key={`${item.id}-${index}`}
                  item={item}
                  index={index}
                />
              ))}
            </Carousel10>
          </div>
        ) : (
          // Load more mode
          <>
            <div
              className={`min-[${MOBILE_BREAKPOINT}px]:hidden flex justify-center items-center gap-spacing-14 flex-wrap`}
            >
              {sortedMetadata?.slice(0, visibleCount)?.map((item, index) => (
                <AccreditationCard
                  key={`${item.id}-${index}`}
                  item={item}
                  index={index}
                />
              ))}
            </div>
            {hasMore && (
              <LoadMore
                className={`min-[${MOBILE_BREAKPOINT}px]:hidden`}
                onClick={handleLoadMore}
                text={load_more_text}
                textColor={load_more_color}
                backgroundColor={""}
                icon={load_more_icon}
                iconAlt={load_more_icon_alt}
              />
            )}
          </>
        )}

        {/* for desktops */}
        <Carousel
          navigationButtonClassName=" w-full justify-between items-center"
          navigationButtonsType={2}
          className={`min-[${MOBILE_BREAKPOINT}px]:flex hidden justify-center items-center sm:w-[934px] md:w-[1054px] lg:w-[1194px] xl:w-[1544px]`}
          carouselClassName="sm:w-[774px] md:w-[820px] lg:w-[938px] xl:w-[1284px]"
          hideNavigationButtons={sortedMetadata?.length <= 4}
          left_navigation_icon={left_navigation_icon}
          left_navigation_icon_alt={left_navigation_icon_alt}
          right_navigation_icon={right_navigation_icon}
          right_navigation_icon_alt={right_navigation_icon_alt}
          onSlideChange={handleSlideChange}
        >
          {sortedMetadata?.map((item, index) => (
            <AccreditationCard
              key={`${item.id}-${index}`}
              item={item}
              index={index}
            />
          ))}
        </Carousel>
      </div>
    </section>
  );
};

export default Accreditation1;
